"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnCfbDeclarativeSpecs = void 0;
const DsnCfbBinarySpec_1 = require("./DsnCfbBinarySpec");
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
var OrcadDsnCfbDeclarativeSpecs;
(function (OrcadDsnCfbDeclarativeSpecs) {
    const RECORD_TERMINATOR = [0xff, 0xe4, 0x5c, 0x39, 0x00, 0x00, 0x00, 0x00];
    const hasBytes = DsnCfbBinarySpec_1.DsnCfbBinarySpec.hasBytes;
    const readUInt16LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt16LE;
    const readInt16LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readInt16LE;
    const readUInt32LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt32LE;
    const readInt32LE = DsnCfbBinarySpec_1.DsnCfbBinarySpec.readInt32LE;
    const bytesToAscii = DsnCfbBinarySpec_1.DsnCfbBinarySpec.bytesToAscii;
    const bytesToHex = DsnCfbBinarySpec_1.DsnCfbBinarySpec.bytesToHex;
    const isPrintableAsciiByte = DsnCfbBinarySpec_1.DsnCfbBinarySpec.isPrintableAsciiByte;
    function hasRecordTerminator(bytes, offset) {
        return hasBytes(bytes, offset, RECORD_TERMINATOR);
    }
    function isPlausibleCoordinate(value) {
        return Number.isInteger(value) && Math.abs(value) <= 1_000_000;
    }
    function arePlausibleCoordinates(values) {
        return values.every(isPlausibleCoordinate);
    }
    function numberField(fields, name) {
        const value = fields[name];
        return typeof value === 'number' ? value : undefined;
    }
    function libraryCatalogValue(ctx, index) {
        const catalog = Array.isArray(ctx.extra?.libraryStringCatalog) ? ctx.extra.libraryStringCatalog : undefined;
        if (catalog === undefined) {
            return undefined;
        }
        const value = catalog[index];
        return typeof value === 'string' && value !== '' ? value : undefined;
    }
    function stringField(fields, name) {
        const value = fields[name];
        return typeof value === 'string' ? value : undefined;
    }
    function stringArrayField(fields, name) {
        const value = fields[name];
        return Array.isArray(value) ? value : [];
    }
    function terminatedPayloadFromContext(ctx) {
        return {
            offset: ctx.payloadOffset ?? ctx.offset,
            payloadByteLength: ctx.payloadByteLength ?? ctx.bytes.byteLength - ctx.offset,
            byteLength: ctx.byteLength ?? ctx.payloadByteLength ?? ctx.bytes.byteLength - ctx.offset,
            payload: ctx.bytes.subarray(ctx.offset),
        };
    }
    function countedStringsFromContext(ctx, options = {}) {
        return OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(ctx.bytes.subarray(ctx.offset), {
            maxCount: options.maxCount ?? 64,
            maxLength: options.maxLength ?? 512,
            requireNullTerminator: options.requireNullTerminator ?? true,
        }).map((string) => ({ ...string, offset: string.offset + ctx.offset }));
    }
    function leadingCountedStringEndFromContext(ctx) {
        const string = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(ctx.bytes, ctx.offset, { maxLength: 256 });
        return string ? OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(string) : undefined;
    }
    function isStreamViewName(value, streamName) {
        return value === streamName || value.endsWith('.Normal') || value.endsWith('.Convert') || value.endsWith('.IEEE');
    }
    function isCapsymPowerSymbolName(name) {
        return name === '0' || /^(?:GND|VCC)/i.test(name);
    }
    OrcadDsnCfbDeclarativeSpecs.isCapsymPowerSymbolName = isCapsymPowerSymbolName;
    function isCacheDefinedConnectionSymbol(symbolName, knownSpecialCacheSymbols) {
        const evidence = knownSpecialCacheSymbols?.get(symbolName);
        if (evidence === undefined) {
            return false;
        }
        return evidence.pinCount >= 1 || evidence.decodeIncomplete;
    }
    function isPinlessCacheSymbol(symbolName, knownSpecialCacheSymbols) {
        const evidence = knownSpecialCacheSymbols?.get(symbolName);
        return evidence !== undefined && evidence.pinCount === 0 && !evidence.decodeIncomplete;
    }
    function isIndeterminateCacheSymbol(symbolName, knownSpecialCacheSymbols) {
        const evidence = knownSpecialCacheSymbols?.get(symbolName);
        return evidence !== undefined && evidence.pinCount === 0 && evidence.decodeIncomplete;
    }
    function isPortConventionSymbolName(symbolName) {
        return /^PORT/i.test(symbolName);
    }
    function isDirectedCacheConnectionSymbol(symbolName, knownSpecialCacheSymbols) {
        const evidence = knownSpecialCacheSymbols?.get(symbolName);
        return evidence?.hasDirectedConnectionPin === true && evidence.directionConflicted !== true;
    }
    function hasConflictedCacheConnectionDirection(symbolName, knownSpecialCacheSymbols) {
        return knownSpecialCacheSymbols?.get(symbolName)?.directionConflicted === true;
    }
    function isPowerCacheConnectionSymbol(symbolName, knownSpecialCacheSymbols) {
        const evidence = knownSpecialCacheSymbols?.get(symbolName);
        return evidence?.hasPowerConnectionPin === true && !evidence.hasDirectedConnectionPin && evidence.pinCount === 1;
    }
    function classifyPlacedSymbolRole(symbolName, extras, streamPath, offset) {
        if (symbolName.startsWith('TitleBlock') || symbolName === 'Cadence') {
            return 'titleblock';
        }
        if (symbolName.startsWith('OFFPAGE') || symbolName.startsWith('PAGE-')) {
            return 'off-page-connector';
        }
        if (symbolName.endsWith('.Normal') || symbolName.endsWith('.Convert') || symbolName.endsWith('.IEEE')) {
            return 'part-inst';
        }
        if (extras.knownSpecialCacheSymbols?.get(symbolName)?.conflictingDefinitions === true) {
            extras.diagnostics?.push({
                severity: 'warning',
                code: 'cache-symbol-definitions-conflict',
                message: `two cache groups named "${symbolName}" draw its connection pin differently, so the name stands for more than one symbol and this placement's kind is unknown`,
                ...(streamPath === undefined ? {} : { streamPath }),
                ...(offset === undefined ? {} : { offset }),
            });
            return 'unknown';
        }
        if (isPowerCacheConnectionSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
            return 'global';
        }
        if (isCapsymPowerSymbolName(symbolName)) {
            extras.diagnostics?.push({
                severity: 'info',
                code: 'global-from-capsym-name',
                message: `no cache group defines a connection pin for "${symbolName}", so it is read as a global on Capture's own CAPSYM naming alone`,
                ...(streamPath === undefined ? {} : { streamPath }),
                ...(offset === undefined ? {} : { offset }),
            });
            return 'global';
        }
        if (!isCacheDefinedConnectionSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
            if (isPinlessCacheSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
                extras.diagnostics?.push({
                    severity: 'info',
                    code: 'cache-symbol-without-pins',
                    message: `cache group named "${symbolName}" decoded no pins, so it is read as package bookkeeping rather than a placed connector`,
                    ...(streamPath === undefined ? {} : { streamPath }),
                    ...(offset === undefined ? {} : { offset }),
                });
            }
            return 'unknown';
        }
        if (isIndeterminateCacheSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
            extras.diagnostics?.push({
                severity: 'warning',
                code: 'cache-symbol-decode-incomplete',
                message: `cache group named "${symbolName}" did not decode cleanly, so its pin structure is indeterminate; the placement is kept on the cache classification alone`,
                ...(streamPath === undefined ? {} : { streamPath }),
                ...(offset === undefined ? {} : { offset }),
            });
        }
        if (isDirectedCacheConnectionSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
            return 'port';
        }
        if (hasConflictedCacheConnectionDirection(symbolName, extras.knownSpecialCacheSymbols)) {
            extras.diagnostics?.push({
                severity: 'warning',
                code: 'connector-role-ambiguous',
                message: `cache definitions named "${symbolName}" disagree about their connection pin's direction, so the name settles no connector kind; the placement is left unclassified`,
                ...(streamPath === undefined ? {} : { streamPath }),
                ...(offset === undefined ? {} : { offset }),
            });
            return 'unknown';
        }
        if (isPortConventionSymbolName(symbolName)) {
            return 'port';
        }
        extras.diagnostics?.push({
            severity: 'warning',
            code: 'ambiguous-connector-role',
            message: `cache-defined connector symbolName="${symbolName}" follows no port naming convention and its pin is neither a power pin nor a directed one, so it is read as an off-page connector`,
            ...(streamPath === undefined ? {} : { streamPath }),
            ...(offset === undefined ? {} : { offset }),
        });
        return 'off-page-connector';
    }
    function wireJunctionsFromMarker(childMarker, startX, startY, endX, endY) {
        const junctions = [];
        if ((childMarker & 0x01) !== 0) {
            junctions.push({ endpoint: 'start', locX: startX, locY: startY });
        }
        if ((childMarker & 0x02) !== 0) {
            junctions.push({ endpoint: 'end', locX: endX, locY: endY });
        }
        return junctions;
    }
    const PAGE_HEADER_MANIFEST_RECORD_TYPE = 0x00036add;
    const UNSET_OBJECT_ID = 0xffff_ffff;
    function placedSymbolDecodeExtras(ctx) {
        const extra = ctx.extra;
        return {
            placementDisplayCounts: extra?.placementDisplayCounts,
            knownSpecialCacheSymbols: extra?.knownSpecialSymbols instanceof Map ? extra.knownSpecialSymbols : undefined,
            diagnostics: extra?.roleDiagnostics,
        };
    }
    function placedSymbolCandidateAt(record, symbolNameLengthOffset, streamPath, extras = {}) {
        const bytes = record.payload;
        if (bytes.length < 32 ||
            bytes.length > 512 ||
            symbolNameLengthOffset < 4 ||
            symbolNameLengthOffset + 2 > bytes.length) {
            return undefined;
        }
        const markerOffset = symbolNameLengthOffset - 4;
        const marker = readUInt32LE(bytes, markerOffset);
        const symbolNameLength = readUInt16LE(bytes, symbolNameLengthOffset);
        const symbolNameStart = symbolNameLengthOffset + 2;
        const symbolNameEnd = symbolNameStart + symbolNameLength;
        if (symbolNameLength < 1 ||
            symbolNameLength > 256 ||
            symbolNameEnd >= bytes.length ||
            bytes[symbolNameEnd] !== 0x00) {
            return undefined;
        }
        for (let offset = symbolNameStart; offset < symbolNameEnd; offset++) {
            if (!isPrintableAsciiByte(bytes[offset])) {
                return undefined;
            }
        }
        const dataOffset = symbolNameEnd + 1;
        if (dataOffset + 18 > bytes.length) {
            return undefined;
        }
        const dbId = readUInt32LE(bytes, dataOffset);
        const locX = readInt16LE(bytes, dataOffset + 12);
        const locY = readInt16LE(bytes, dataOffset + 14);
        const color = bytes[dataOffset + 16];
        const flags = bytes[dataOffset + 17];
        if (dbId === 0 || dbId === UNSET_OBJECT_ID || color > 255 || !arePlausibleCoordinates([locX, locY])) {
            return undefined;
        }
        const symbolName = bytesToAscii(bytes, symbolNameStart, symbolNameEnd);
        if (symbolName === '0' && color !== 48) {
            return undefined;
        }
        const role = classifyPlacedSymbolRole(symbolName, extras, streamPath, record.offset);
        if (role === 'unknown' && !isCacheDefinedConnectionSymbol(symbolName, extras.knownSpecialCacheSymbols)) {
            extras.diagnostics?.push({
                severity: 'info',
                code: 'placed-symbol-role-rejected',
                message: `placed-symbol candidate dropped: unrecognized role for symbolName="${symbolName}" dbId=${dbId}`,
                streamPath,
                offset: record.offset,
            });
            return undefined;
        }
        return {
            role,
            displayCountOffset: record.offset + dataOffset + 20,
            symbolName,
            graphicName: symbolName,
            dbId,
            locX,
            locY,
            color,
            recordType: readUInt32LE(bytes, 0),
            marker,
            flags,
        };
    }
    function isEmbeddedPlacedSymbolName(value, extras) {
        if (/\.(?:Normal|IEEE)$/.test(value)) {
            return true;
        }
        if (/^(?:PORT|OFFPAGE)/i.test(value)) {
            return true;
        }
        return isCacheDefinedConnectionSymbol(value, extras.knownSpecialCacheSymbols);
    }
    function placedSymbolCandidatesFromContext(ctx) {
        const record = terminatedPayloadFromContext(ctx);
        const extras = placedSymbolDecodeExtras(ctx);
        const simpleSymbol = placedSymbolCandidateAt(record, 8, ctx.streamPath, extras);
        if (simpleSymbol) {
            return [simpleSymbol];
        }
        if (readUInt32LE(record.payload, 0) === PAGE_HEADER_MANIFEST_RECORD_TYPE) {
            return [];
        }
        for (const string of OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.extractLengthPrefixedAsciiStrings(record.payload, {
            maxCount: 64,
            maxLength: 256,
        })) {
            if (string.offset === 8 || !isEmbeddedPlacedSymbolName(string.value, extras)) {
                continue;
            }
            const embeddedSymbol = placedSymbolCandidateAt(record, string.offset, ctx.streamPath, extras);
            if (embeddedSymbol) {
                return [embeddedSymbol];
            }
        }
        for (let offset = 4; offset + 2 <= record.payload.length; offset++) {
            const length = readUInt16LE(record.payload, offset);
            const end = offset + 2 + length;
            if (length < 8 || end >= record.payload.length || record.payload[end] !== 0x00) {
                continue;
            }
            const name = bytesToAscii(record.payload, offset + 2, end);
            if (!name.endsWith('.Normal') && !name.endsWith('.Convert') && !name.endsWith('.IEEE')) {
                continue;
            }
            const nestedSymbol = placedSymbolCandidateAt(record, offset, ctx.streamPath, extras);
            if (nestedSymbol) {
                return [nestedSymbol];
            }
        }
        return [];
    }
    function placedSymbolCandidateField(fields) {
        const candidates = fields.candidates;
        return Array.isArray(candidates) ? candidates[0] : undefined;
    }
    function propertyIdPairsAt(payload, count) {
        const pairsOffset = payload.length - count * 8;
        const pairs = [];
        for (let index = 0; index < count; index++) {
            const nameId = readUInt32LE(payload, pairsOffset + index * 8);
            const valueId = readUInt32LE(payload, pairsOffset + index * 8 + 4);
            if (nameId === 0 || nameId > 10_000_000 || valueId > 10_000_000) {
                return undefined;
            }
            pairs.push({ nameId, valueId });
        }
        return pairs;
    }
    const PropertyIdPairBlockTags = new Set([0x0d, 0x41]);
    function decodeTailPropertyIdPairs(payload) {
        const maxPairs = Math.min(512, Math.floor((payload.length - 3) / 8));
        let unrecognizedTag;
        for (let count = 1; count <= maxPairs; count++) {
            const headerOffset = payload.length - count * 8 - 3;
            if (readUInt16LE(payload, headerOffset + 1) !== count) {
                continue;
            }
            const pairs = propertyIdPairsAt(payload, count);
            if (!pairs) {
                continue;
            }
            if (PropertyIdPairBlockTags.has(payload[headerOffset])) {
                return { pairs: pairs.reverse() };
            }
            unrecognizedTag = unrecognizedTag ?? payload[headerOffset];
        }
        return unrecognizedTag === undefined ? {} : { unrecognizedTag };
    }
    OrcadDsnCfbDeclarativeSpecs.decodeTailPropertyIdPairs = decodeTailPropertyIdPairs;
    function tailPinNumberMapFromContext(ctx) {
        const scanWork = ctx.extra?.symbolScanWork;
        const bytes = ctx.bytes;
        const offset = ctx.offset;
        const byteLength = ctx.byteLength ?? bytes.length - offset;
        if (byteLength < 10) {
            return undefined;
        }
        let cursor = offset;
        let packageName;
        for (let candidateOffset = offset; candidateOffset < Math.min(offset + 16, offset + byteLength); candidateOffset++) {
            if (scanWork) {
                scanWork.mapStringProbes++;
            }
            const candidate = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, candidateOffset, {
                maxLength: 256,
            });
            if (candidate) {
                packageName = candidate;
                cursor = candidateOffset;
                break;
            }
        }
        if (!packageName) {
            return undefined;
        }
        cursor += 2 + packageName.length + (packageName.hasNullTerminator ? 1 : 0);
        if (cursor + 2 > offset + byteLength) {
            return undefined;
        }
        const count = readUInt16LE(bytes, cursor);
        cursor += 2;
        if (count < 1 || count > 4096) {
            return undefined;
        }
        const strings = [packageName];
        const entries = [];
        for (let position = 0; position < count && cursor < offset + byteLength; position++) {
            if (scanWork) {
                scanWork.mapStringProbes++;
            }
            let number = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(bytes, cursor, { maxLength: 64 });
            if (!number &&
                cursor + 3 <= offset + byteLength &&
                readUInt16LE(bytes, cursor) === 0 &&
                bytes[cursor + 2] === 0) {
                number = { offset: cursor, length: 0, value: '', hasNullTerminator: true };
            }
            if (!number) {
                return undefined;
            }
            strings.push(number);
            entries.push({ position, number: number.value });
            cursor += 2 + number.length + (number.hasNullTerminator ? 1 : 0);
            if (cursor < offset + byteLength && bytes[cursor] === 0x7f) {
                cursor++;
            }
        }
        if (entries.length !== count) {
            return undefined;
        }
        if (!entries.some((entry) => entry.number.length > 0)) {
            return undefined;
        }
        const consumedByteLength = cursor - packageName.offset;
        return {
            offset: packageName.offset,
            byteLength: consumedByteLength,
            payloadByteLength: consumedByteLength,
            packageName: packageName.value,
            entries,
            strings,
        };
    }
    const primitiveBitmapRecordSpec = {
        name: 'primitive.bitmap',
        family: 'primitive',
        kind: 'bitmap',
        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2f, 0x2f])],
        fields: {
            opcode: { kind: 'bytes', offset: 0, length: 4 },
            locX: { kind: 'i32le', offset: 10 },
            locY: { kind: 'i32le', offset: 14 },
            x2: { kind: 'i32le', offset: 18 },
            y2: { kind: 'i32le', offset: 22 },
            x1: { kind: 'i32le', offset: 26 },
            y1: { kind: 'i32le', offset: 30 },
            pixelWidth: { kind: 'i32le', offset: 34 },
            pixelHeight: { kind: 'i32le', offset: 38 },
            dibByteLength: { kind: 'u32le', offset: 42 },
            dibHeaderSize: { kind: 'u32le', offset: 46 },
            dibPayload: {
                kind: 'bytes',
                offset: 46,
                length: (ctx) => (typeof ctx.fields?.dibByteLength === 'number' ? ctx.fields.dibByteLength : undefined),
                optional: true,
            },
        },
        derive: function materializeBitmapPrimitive(fields, ctx) {
            const { bytes, offset } = ctx;
            if (!hasBytes(bytes, offset, [0x2f, 0x2f]) || offset + 54 > bytes.length) {
                return undefined;
            }
            const dibByteLength = numberField(fields, 'dibByteLength');
            const dibHeaderSize = numberField(fields, 'dibHeaderSize');
            const dibOffset = offset + 46;
            const terminatorOffset = dibOffset + (dibByteLength ?? 0);
            if (dibByteLength === undefined ||
                dibHeaderSize === undefined ||
                dibByteLength < 1 ||
                ![12, 40, 108, 124].includes(dibHeaderSize) ||
                dibByteLength > bytes.length - dibOffset - RECORD_TERMINATOR.length ||
                !hasRecordTerminator(bytes, terminatorOffset)) {
                return undefined;
            }
            const locX = numberField(fields, 'locX');
            const locY = numberField(fields, 'locY');
            const x2 = numberField(fields, 'x2');
            const y2 = numberField(fields, 'y2');
            const x1 = numberField(fields, 'x1');
            const y1 = numberField(fields, 'y1');
            const pixelWidth = numberField(fields, 'pixelWidth');
            const pixelHeight = numberField(fields, 'pixelHeight');
            if (locX === undefined ||
                locY === undefined ||
                x1 === undefined ||
                y1 === undefined ||
                x2 === undefined ||
                y2 === undefined ||
                pixelWidth === undefined ||
                pixelHeight === undefined) {
                return undefined;
            }
            if (!arePlausibleCoordinates([locX, locY, x1, y1, x2, y2, pixelWidth, pixelHeight])) {
                return undefined;
            }
            return {
                kind: 'bitmap',
                offset,
                byteLength: terminatorOffset + RECORD_TERMINATOR.length - offset,
                opcode: bytesToHex(bytes, offset, offset + 4),
                ...(offset >= 73 ? { dbId: readUInt32LE(bytes, offset - 73) } : {}),
                locX,
                locY,
                x1,
                y1,
                x2,
                y2,
                pixelWidth,
                pixelHeight,
                dibByteLength,
                dibPayload: bytes.slice(dibOffset, terminatorOffset),
            };
        },
    };
    const primitiveCommentTextRecordSpec = {
        name: 'primitive.comment-text',
        family: 'primitive',
        kind: 'comment-text',
        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2e, 0x2e])],
        fields: {
            opcode: { kind: 'bytes', offset: 0, length: 3 },
            x1: { kind: 'i32le', offset: 10 },
            y1: { kind: 'i32le', offset: 14 },
            x2: { kind: 'i32le', offset: 18 },
            y2: { kind: 'i32le', offset: 22 },
            locX: { kind: 'i32le', offset: 26 },
            locY: { kind: 'i32le', offset: 30 },
            textStyleId: { kind: 'u16le', offset: 34 },
            textFlags: { kind: 'u16le', offset: 36 },
            nameLength: { kind: 'u16le', offset: 38 },
            name: {
                kind: 'ascii',
                offset: 40,
                length: (ctx) => (typeof ctx.fields?.nameLength === 'number' ? ctx.fields.nameLength : undefined),
            },
        },
        derive: function materializeCommentTextPrimitive(fields, ctx) {
            const { bytes, offset } = ctx;
            if (offset + 49 > bytes.length || bytes[offset] !== 0x2e || bytes[offset + 1] !== 0x2e) {
                return undefined;
            }
            const nameLength = numberField(fields, 'nameLength');
            const name = stringField(fields, 'name');
            const nameStart = offset + 40;
            const nameEnd = nameStart + (nameLength ?? 0);
            const terminatorOffset = nameEnd + 1;
            if (nameLength === undefined ||
                name === undefined ||
                nameLength < 1 ||
                nameLength > 4096 ||
                terminatorOffset > bytes.length ||
                bytes[nameEnd] !== 0x00) {
                return undefined;
            }
            const hasTerminator = terminatorOffset + RECORD_TERMINATOR.length <= bytes.length && hasRecordTerminator(bytes, terminatorOffset);
            const x1 = numberField(fields, 'x1');
            const y1 = numberField(fields, 'y1');
            const x2 = numberField(fields, 'x2');
            const y2 = numberField(fields, 'y2');
            const locX = numberField(fields, 'locX');
            const locY = numberField(fields, 'locY');
            const textStyleId = numberField(fields, 'textStyleId');
            const textFlags = numberField(fields, 'textFlags');
            if (x1 === undefined ||
                y1 === undefined ||
                x2 === undefined ||
                y2 === undefined ||
                locX === undefined ||
                locY === undefined ||
                textStyleId === undefined ||
                textFlags === undefined) {
                return undefined;
            }
            if (!arePlausibleCoordinates([x1, y1, x2, y2, locX, locY])) {
                return undefined;
            }
            return {
                kind: 'comment-text',
                offset,
                byteLength: terminatorOffset + (hasTerminator ? RECORD_TERMINATOR.length : 0) - offset,
                opcode: bytesToHex(bytes, offset, offset + 3),
                name,
                x1,
                y1,
                x2,
                y2,
                locX,
                locY,
                textStyleId,
                textFlags,
            };
        },
    };
    const primitiveRectRecordSpec = {
        name: 'primitive.rect',
        family: 'primitive',
        kind: 'rect',
        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x28, 0x28, 0x28])],
        fields: {
            opcode: { kind: 'bytes', offset: 0, length: 3 },
            x1: { kind: 'i32le', offset: 10 },
            y1: { kind: 'i32le', offset: 14 },
            x2: { kind: 'i32le', offset: 18 },
            y2: { kind: 'i32le', offset: 22 },
            lineStyle: { kind: 'i32le', offset: 26 },
            lineWidth: { kind: 'i32le', offset: 30 },
            fillStyle: { kind: 'i32le', offset: 34 },
            hatchStyle: { kind: 'i32le', offset: 38 },
        },
        derive: function materializeRectPrimitive(fields, ctx) {
            const { bytes, offset } = ctx;
            if (!hasBytes(bytes, offset, [0x28, 0x28, 0x28]) || !hasRecordTerminator(bytes, offset + 42)) {
                return undefined;
            }
            const x1 = numberField(fields, 'x1');
            const y1 = numberField(fields, 'y1');
            const x2 = numberField(fields, 'x2');
            const y2 = numberField(fields, 'y2');
            const lineStyle = numberField(fields, 'lineStyle');
            const lineWidth = numberField(fields, 'lineWidth');
            const fillStyle = numberField(fields, 'fillStyle');
            const hatchStyle = numberField(fields, 'hatchStyle');
            if (x1 === undefined ||
                y1 === undefined ||
                x2 === undefined ||
                y2 === undefined ||
                lineStyle === undefined ||
                lineWidth === undefined ||
                fillStyle === undefined ||
                hatchStyle === undefined) {
                return undefined;
            }
            if (!arePlausibleCoordinates([x1, y1, x2, y2])) {
                return undefined;
            }
            return {
                kind: 'rect',
                offset,
                byteLength: 50,
                opcode: bytesToHex(bytes, offset, offset + 3),
                x1,
                y1,
                x2,
                y2,
                lineStyle,
                lineWidth,
                fillStyle,
                hatchStyle,
            };
        },
    };
    const primitiveEllipseRecordSpec = {
        name: 'primitive.ellipse',
        family: 'primitive',
        kind: 'ellipse',
        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2b, 0x2b, 0x28])],
        fields: {
            opcode: { kind: 'bytes', offset: 0, length: 3 },
            x1: { kind: 'i32le', offset: 10 },
            y1: { kind: 'i32le', offset: 14 },
            x2: { kind: 'i32le', offset: 18 },
            y2: { kind: 'i32le', offset: 22 },
            lineStyle: { kind: 'i32le', offset: 26 },
            lineWidth: { kind: 'i32le', offset: 30 },
            fillStyle: { kind: 'i32le', offset: 34 },
            hatchStyle: { kind: 'i32le', offset: 38 },
        },
        derive: function materializeEllipsePrimitive(fields, ctx) {
            const { bytes, offset } = ctx;
            if (!hasBytes(bytes, offset, [0x2b, 0x2b, 0x28]) || !hasRecordTerminator(bytes, offset + 42)) {
                return undefined;
            }
            const x1 = numberField(fields, 'x1');
            const y1 = numberField(fields, 'y1');
            const x2 = numberField(fields, 'x2');
            const y2 = numberField(fields, 'y2');
            const lineStyle = numberField(fields, 'lineStyle');
            const lineWidth = numberField(fields, 'lineWidth');
            const fillStyle = numberField(fields, 'fillStyle');
            const hatchStyle = numberField(fields, 'hatchStyle');
            if (x1 === undefined ||
                y1 === undefined ||
                x2 === undefined ||
                y2 === undefined ||
                lineStyle === undefined ||
                lineWidth === undefined ||
                fillStyle === undefined ||
                hatchStyle === undefined) {
                return undefined;
            }
            if (!arePlausibleCoordinates([x1, y1, x2, y2])) {
                return undefined;
            }
            return {
                kind: 'ellipse',
                offset,
                byteLength: 50,
                opcode: bytesToHex(bytes, offset, offset + 3),
                x1,
                y1,
                x2,
                y2,
                lineStyle,
                lineWidth,
                fillStyle,
                hatchStyle,
            };
        },
    };
    const primitivePolygonRecordSpec = {
        name: 'primitive.polygon',
        family: 'primitive',
        kind: 'polygon',
        match: [
            (ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2c, 0x2c, 0x3a]) ||
                hasBytes(ctx.bytes, ctx.offset, [0x2c, 0x2c, 0x2e]) ||
                hasBytes(ctx.bytes, ctx.offset, [0x2c, 0x2c, 0x2a]),
        ],
        fields: {
            opcode: { kind: 'bytes', offset: 0, length: 3 },
            fillStyle: { kind: 'i32le', offset: 10 },
            hatchStyle: { kind: 'i32le', offset: 14 },
            lineStyle: { kind: 'i32le', offset: 18 },
            lineWidth: { kind: 'i32le', offset: 22 },
            pointCount: { kind: 'u16le', offset: 26 },
            points: {
                kind: 'repeated',
                offset: 28,
                count: (ctx) => (typeof ctx.fields?.pointCount === 'number' ? ctx.fields.pointCount : undefined),
                byteLength: 4,
                fields: {
                    y: { kind: 'i16le', offset: 0 },
                    x: { kind: 'i16le', offset: 2 },
                },
            },
        },
        derive: function materializePolygonPrimitive(fields, ctx) {
            const { bytes, offset } = ctx;
            if ((!hasBytes(bytes, offset, [0x2c, 0x2c, 0x3a]) &&
                !hasBytes(bytes, offset, [0x2c, 0x2c, 0x2e]) &&
                !hasBytes(bytes, offset, [0x2c, 0x2c, 0x2a])) ||
                offset + 28 > bytes.length) {
                return undefined;
            }
            const pointCount = numberField(fields, 'pointCount');
            const pointsOffset = offset + 28;
            const terminatorOffset = pointsOffset + (pointCount ?? 0) * 4;
            if (pointCount === undefined ||
                pointCount < 2 ||
                pointCount > 4096 ||
                !hasRecordTerminator(bytes, terminatorOffset)) {
                return undefined;
            }
            const points = fields.points;
            const fillStyle = numberField(fields, 'fillStyle');
            const hatchStyle = numberField(fields, 'hatchStyle');
            const lineStyle = numberField(fields, 'lineStyle');
            const lineWidth = numberField(fields, 'lineWidth');
            if (!points ||
                fillStyle === undefined ||
                hatchStyle === undefined ||
                lineStyle === undefined ||
                lineWidth === undefined) {
                return undefined;
            }
            const normalizedPoints = points.map((point) => ({
                x: typeof point.x === 'number' ? point.x : Number.NaN,
                y: typeof point.y === 'number' ? point.y : Number.NaN,
            }));
            if (!normalizedPoints.every((point) => arePlausibleCoordinates([point.x, point.y]))) {
                return undefined;
            }
            if (hasBytes(bytes, offset, [0x2c, 0x2c, 0x2a]) &&
                (pointCount !== 4 || [fillStyle, hatchStyle, lineStyle, lineWidth].some((value) => value !== 0))) {
                return undefined;
            }
            const isCompact = hasBytes(bytes, offset, [0x2c, 0x2c, 0x2e]);
            return {
                kind: 'polygon',
                offset,
                byteLength: terminatorOffset + RECORD_TERMINATOR.length - offset,
                opcode: bytesToHex(bytes, offset, offset + 3),
                fillStyle: isCompact ? lineStyle : fillStyle,
                hatchStyle,
                lineStyle: isCompact ? fillStyle : lineStyle,
                lineWidth,
                points: normalizedPoints,
            };
        },
    };
    const POLYLINE_MIN_POINTS = 2;
    const POLYLINE_MAX_POINTS = 4096;
    function polylineFramingAt(bytes, offset) {
        if (!hasBytes(bytes, offset, [0x2d, 0x2d]) || offset + 20 > bytes.length) {
            return undefined;
        }
        const pointCount = readUInt16LE(bytes, offset + 18);
        if (pointCount < POLYLINE_MIN_POINTS || pointCount > POLYLINE_MAX_POINTS) {
            return undefined;
        }
        const terminatorOffset = offset + 20 + pointCount * 4;
        const byteLength = terminatorOffset + RECORD_TERMINATOR.length - offset;
        if (byteLength !== bytes[offset + 2] + 10 || !hasRecordTerminator(bytes, terminatorOffset)) {
            return undefined;
        }
        return { pointCount, byteLength };
    }
    const primitivePolylineRecordSpec = {
        name: 'primitive.polyline',
        family: 'primitive',
        kind: 'polyline',
        match: [(ctx) => polylineFramingAt(ctx.bytes, ctx.offset) !== undefined],
        fields: {
            opcode: { kind: 'bytes', offset: 0, length: 3 },
            declaredLength: { kind: 'u8', offset: 2 },
            lineStyle: { kind: 'i32le', offset: 10 },
            lineWidth: { kind: 'i32le', offset: 14 },
            pointCount: { kind: 'u16le', offset: 18 },
            points: {
                kind: 'repeated',
                offset: 20,
                count: (ctx) => polylineFramingAt(ctx.bytes, ctx.offset)?.pointCount,
                byteLength: 4,
                fields: {
                    y: { kind: 'i16le', offset: 0 },
                    x: { kind: 'i16le', offset: 2 },
                },
            },
        },
        derive: function materializePolylinePrimitive(fields, ctx) {
            const { bytes, offset } = ctx;
            const framing = polylineFramingAt(bytes, offset);
            if (!framing) {
                return undefined;
            }
            const { byteLength } = framing;
            const points = fields.points;
            const lineStyle = numberField(fields, 'lineStyle');
            const lineWidth = numberField(fields, 'lineWidth');
            if (!points || lineStyle === undefined || lineWidth === undefined) {
                return undefined;
            }
            const normalizedPoints = points.map((point) => ({
                x: typeof point.x === 'number' ? point.x : Number.NaN,
                y: typeof point.y === 'number' ? point.y : Number.NaN,
            }));
            if (!normalizedPoints.every((point) => arePlausibleCoordinates([point.x, point.y]))) {
                return undefined;
            }
            return {
                kind: 'polyline',
                offset,
                byteLength,
                opcode: bytesToHex(bytes, offset, offset + 3),
                lineStyle,
                lineWidth,
                points: normalizedPoints,
            };
        },
    };
    const primitiveArcRecordSpec = {
        name: 'primitive.arc',
        family: 'primitive',
        kind: 'arc',
        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x2a, 0x2a, 0x30])],
        fields: {
            opcode: { kind: 'bytes', offset: 0, length: 3 },
            x1: { kind: 'i32le', offset: 10 },
            y1: { kind: 'i32le', offset: 14 },
            x2: { kind: 'i32le', offset: 18 },
            y2: { kind: 'i32le', offset: 22 },
            startX: { kind: 'i32le', offset: 26 },
            startY: { kind: 'i32le', offset: 30 },
            endX: { kind: 'i32le', offset: 34 },
            endY: { kind: 'i32le', offset: 38 },
            lineStyle: { kind: 'i32le', offset: 42 },
            lineWidth: { kind: 'i32le', offset: 46 },
        },
        derive: function materializeArcPrimitive(fields, ctx) {
            const { bytes, offset } = ctx;
            if (!hasBytes(bytes, offset, [0x2a, 0x2a, 0x30]) || !hasRecordTerminator(bytes, offset + 50)) {
                return undefined;
            }
            const x1 = numberField(fields, 'x1');
            const y1 = numberField(fields, 'y1');
            const x2 = numberField(fields, 'x2');
            const y2 = numberField(fields, 'y2');
            const startX = numberField(fields, 'startX');
            const startY = numberField(fields, 'startY');
            const endX = numberField(fields, 'endX');
            const endY = numberField(fields, 'endY');
            const lineStyle = numberField(fields, 'lineStyle');
            const lineWidth = numberField(fields, 'lineWidth');
            if (x1 === undefined ||
                y1 === undefined ||
                x2 === undefined ||
                y2 === undefined ||
                startX === undefined ||
                startY === undefined ||
                endX === undefined ||
                endY === undefined ||
                lineStyle === undefined ||
                lineWidth === undefined) {
                return undefined;
            }
            if (!arePlausibleCoordinates([x1, y1, x2, y2, startX, startY, endX, endY])) {
                return undefined;
            }
            return {
                kind: 'arc',
                offset,
                byteLength: 58,
                opcode: bytesToHex(bytes, offset, offset + 3),
                x1,
                y1,
                x2,
                y2,
                startX,
                startY,
                endX,
                endY,
                lineStyle,
                lineWidth,
            };
        },
    };
    const primitiveLineRecordSpec = {
        name: 'primitive.line',
        family: 'primitive',
        kind: 'line',
        match: [(ctx) => hasBytes(ctx.bytes, ctx.offset, [0x29, 0x29, 0x20])],
        fields: {
            opcode: { kind: 'bytes', offset: 0, length: 3 },
            x1: { kind: 'i32le', offset: 10 },
            y1: { kind: 'i32le', offset: 14 },
            x2: { kind: 'i32le', offset: 18 },
            y2: { kind: 'i32le', offset: 22 },
            lineStyle: { kind: 'i32le', offset: 26 },
            lineWidth: { kind: 'i32le', offset: 30 },
        },
        derive: function materializeLinePrimitive(fields, ctx) {
            const { bytes, offset } = ctx;
            if (!hasBytes(bytes, offset, [0x29, 0x29, 0x20]) || !hasRecordTerminator(bytes, offset + 34)) {
                return undefined;
            }
            const x1 = numberField(fields, 'x1');
            const y1 = numberField(fields, 'y1');
            const x2 = numberField(fields, 'x2');
            const y2 = numberField(fields, 'y2');
            const lineStyle = numberField(fields, 'lineStyle');
            const lineWidth = numberField(fields, 'lineWidth');
            if (x1 === undefined ||
                y1 === undefined ||
                x2 === undefined ||
                y2 === undefined ||
                lineStyle === undefined ||
                lineWidth === undefined) {
                return undefined;
            }
            if (!arePlausibleCoordinates([x1, y1, x2, y2])) {
                return undefined;
            }
            return {
                kind: 'line',
                offset,
                byteLength: 42,
                opcode: bytesToHex(bytes, offset, offset + 3),
                x1,
                y1,
                x2,
                y2,
                lineStyle,
                lineWidth,
            };
        },
    };
    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PRIMITIVE_RECORD_SPECS = [
        primitiveBitmapRecordSpec,
        primitiveCommentTextRecordSpec,
        primitiveRectRecordSpec,
        primitiveEllipseRecordSpec,
        primitivePolygonRecordSpec,
        primitivePolylineRecordSpec,
        primitiveArcRecordSpec,
        primitiveLineRecordSpec,
    ];
    const pageWireRecordSpec = {
        name: 'page.wire',
        family: 'page',
        kind: 'wire',
        match: [(ctx) => ctx.bytes.length >= 52 && ctx.bytes.length <= 2048],
        fields: {
            dbId: { kind: 'u32le', offset: 0 },
            metadataId: { kind: 'u32le', offset: 4 },
            color: { kind: 'i32le', offset: 8 },
            startX: { kind: 'i32le', offset: 12 },
            startY: { kind: 'i32le', offset: 16 },
            endX: { kind: 'i32le', offset: 20 },
            endY: { kind: 'i32le', offset: 24 },
            childMarker: { kind: 'u32le', offset: 28 },
            lineWidth: { kind: 'u8', offset: 33, optional: true },
            lineStyle: { kind: 'u8', offset: 37, optional: true },
            wireType: { kind: 'u8', offset: 41, optional: true },
        },
        derive: function materializeWirePageObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const bytes = record.payload;
            if (bytes.length < 52 || bytes.length > 2048) {
                return undefined;
            }
            const dbId = numberField(fields, 'dbId');
            const metadataId = numberField(fields, 'metadataId');
            const color = numberField(fields, 'color');
            const startX = numberField(fields, 'startX');
            const startY = numberField(fields, 'startY');
            const endX = numberField(fields, 'endX');
            const endY = numberField(fields, 'endY');
            const childMarker = numberField(fields, 'childMarker');
            const lineWidth = numberField(fields, 'lineWidth');
            const lineStyle = numberField(fields, 'lineStyle');
            const wireType = numberField(fields, 'wireType');
            if (dbId === undefined ||
                metadataId === undefined ||
                color === undefined ||
                startX === undefined ||
                startY === undefined ||
                endX === undefined ||
                endY === undefined ||
                childMarker === undefined ||
                dbId <= 0 ||
                dbId > 1_000_000_000 ||
                metadataId <= 0 ||
                metadataId > 1_000_000_000 ||
                color < 0 ||
                color > 255 ||
                !arePlausibleCoordinates([startX, startY, endX, endY])) {
                return undefined;
            }
            const junctions = wireJunctionsFromMarker(childMarker, startX, startY, endX, endY);
            return {
                kind: 'wire',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                dbId,
                metadataId,
                color,
                startX,
                startY,
                endX,
                endY,
                childMarker,
                ...(wireType !== undefined && wireType <= 255 ? { wireType } : {}),
                ...(junctions.length > 0 ? { junctions } : {}),
                ...((bytes.length !== 52 &&
                    lineWidth !== undefined &&
                    lineStyle !== undefined &&
                    lineWidth <= 20 &&
                    lineStyle <= 20) ?
                    { lineWidth, lineStyle }
                    : {}),
            };
        },
    };
    const BUS_ENTRY_RECORD_MARKER = 0x30;
    const pageBusEntryRecordSpec = {
        name: 'page.bus-entry',
        family: 'page',
        kind: 'bus-entry',
        match: [
            (ctx) => ctx.bytes.length >= 20 && ctx.bytes.length <= 64,
            (ctx) => readUInt32LE(ctx.bytes, 0) === BUS_ENTRY_RECORD_MARKER,
            (ctx) => {
                const dx = Math.abs(readInt32LE(ctx.bytes, 4) - readInt32LE(ctx.bytes, 12));
                const dy = Math.abs(readInt32LE(ctx.bytes, 8) - readInt32LE(ctx.bytes, 16));
                return dx > 0 && dx === dy;
            },
        ],
        fields: {
            busX: { kind: 'i32le', offset: 4 },
            busY: { kind: 'i32le', offset: 8 },
            endX: { kind: 'i32le', offset: 12 },
            endY: { kind: 'i32le', offset: 16 },
        },
        derive: function materializeBusEntryPageObject(fields) {
            const busX = fields.busX;
            const busY = fields.busY;
            const endX = fields.endX;
            const endY = fields.endY;
            if (typeof busX !== 'number' ||
                typeof busY !== 'number' ||
                typeof endX !== 'number' ||
                typeof endY !== 'number' ||
                !arePlausibleCoordinates([busX, busY, endX, endY])) {
                return undefined;
            }
            return { kind: 'bus-entry', offset: 0, byteLength: 0, payloadByteLength: 0, busX, busY, endX, endY };
        },
    };
    const DRAWN_PORT_REQUIRED_STYLE_FLAGS = 0x21;
    const DRAWN_PORT_KNOWN_STYLE_FLAGS = 0x79;
    const DRAWN_PORT_NET_STYLE_FLAG = 0x40;
    const pageDrawnPortRecordSpec = {
        name: 'page.drawn-port',
        family: 'page',
        kind: 'drawn-port',
        match: [(ctx) => ctx.bytes.length >= 48 && ctx.bytes.length <= 192],
        fields: {
            name: { kind: 'counted-ascii', offset: 0, maxLength: 256 },
            dataOffset: {
                kind: 'custom',
                offset: 0,
                read: leadingCountedStringEndFromContext,
            },
            startX: {
                kind: 'i32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset : undefined),
            },
            startY: {
                kind: 'i32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 4 : undefined),
            },
            hotptX: {
                kind: 'i32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 8 : undefined),
            },
            hotptY: {
                kind: 'i32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 12 : undefined),
            },
            styleFlags: {
                kind: 'u8',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 16 : undefined),
            },
            type: {
                kind: 'u16le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 20 : undefined),
                optional: true,
            },
            countedStrings: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 4, maxLength: 256 }),
            },
            asciiRuns: { kind: 'ascii-runs', offset: 0, minLength: 4, maxCount: 8 },
        },
        derive: function materializeDrawnPortPageObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const bytes = record.payload;
            if (bytes.length < 48 || bytes.length > 192) {
                return undefined;
            }
            const nameValue = stringField(fields, 'name');
            if (!nameValue) {
                return undefined;
            }
            const dataOffset = numberField(fields, 'dataOffset');
            if (dataOffset === undefined) {
                return undefined;
            }
            if (dataOffset + 25 > bytes.length) {
                return undefined;
            }
            const startX = numberField(fields, 'startX');
            const startY = numberField(fields, 'startY');
            const hotptX = numberField(fields, 'hotptX');
            const hotptY = numberField(fields, 'hotptY');
            const styleFlags = numberField(fields, 'styleFlags');
            if (startX === undefined ||
                startY === undefined ||
                hotptX === undefined ||
                hotptY === undefined ||
                styleFlags === undefined) {
                return undefined;
            }
            if (!arePlausibleCoordinates([startX, startY, hotptX, hotptY])) {
                return undefined;
            }
            if ((styleFlags & DRAWN_PORT_REQUIRED_STYLE_FLAGS) !== DRAWN_PORT_REQUIRED_STYLE_FLAGS ||
                (styleFlags & ~DRAWN_PORT_KNOWN_STYLE_FLAGS) !== 0) {
                placedSymbolDecodeExtras(ctx).diagnostics?.push({
                    severity: 'info',
                    code: 'drawn-port-style-flags-unknown',
                    message: `a sheet-symbol pin record named "${nameValue}" carries style flags 0x${styleFlags.toString(16)}, which is no combination the format is known to write`,
                    streamPath: ctx.streamPath,
                    offset: record.offset,
                });
                return undefined;
            }
            const type = numberField(fields, 'type');
            const ownerName = stringArrayField(fields, 'countedStrings').find((string) => string.offset > dataOffset + 32);
            return {
                kind: 'drawn-port',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                name: nameValue,
                startX,
                startY,
                hotptX,
                hotptY,
                ...(type !== undefined && type <= 32 ? { type } : {}),
                visible: true,
                isNetStyle: (styleFlags & DRAWN_PORT_NET_STYLE_FLAG) !== 0,
                isBus: nameValue.includes('['),
                ...(ownerName ? { ownerName: ownerName.value } : {}),
            };
        },
    };
    const pageAliasRecordSpec = {
        name: 'page.alias',
        family: 'page',
        kind: 'alias',
        match: [(ctx) => ctx.bytes.length >= 32],
        fields: {
            locX: { kind: 'i32le', offset: 0 },
            locY: { kind: 'i32le', offset: 4 },
            color: { kind: 'i32le', offset: 8 },
            rotation: { kind: 'i32le', offset: 12 },
            name: {
                kind: 'counted-ascii',
                offset: 20,
                maxLength: 512,
            },
        },
        derive: function materializeAliasPageObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const bytes = record.payload;
            if (bytes.length < 32) {
                return undefined;
            }
            const locX = numberField(fields, 'locX');
            const locY = numberField(fields, 'locY');
            const color = numberField(fields, 'color');
            const rotation = numberField(fields, 'rotation');
            const name = stringField(fields, 'name');
            if (locX === undefined ||
                locY === undefined ||
                color === undefined ||
                rotation === undefined ||
                name === undefined ||
                color < 0 ||
                color > 255 ||
                rotation < 0 ||
                rotation > 3 ||
                !arePlausibleCoordinates([locX, locY])) {
                return undefined;
            }
            return {
                kind: 'alias',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                name,
                locX,
                locY,
                color,
                rotation,
            };
        },
    };
    const pagePlacedSymbolRecordSpec = {
        name: 'page.placed-symbol',
        family: 'page',
        kind: 'placed-symbol',
        match: [(ctx) => ctx.bytes.length >= 32 && ctx.bytes.length <= 512],
        fields: {
            recordType: { kind: 'u32le', offset: 0 },
            marker: { kind: 'u32le', offset: 4, optional: true },
            candidates: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => placedSymbolCandidatesFromContext(ctx),
            },
            strings: { kind: 'ascii-runs', offset: 0, minLength: 4, maxCount: 16 },
        },
        derive: function materializePlacedSymbolPageObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const candidate = placedSymbolCandidateField(fields);
            if (!candidate) {
                return undefined;
            }
            const { displayCountOffset, ...semanticCandidate } = candidate;
            placedSymbolDecodeExtras(ctx).placementDisplayCounts?.set(record.offset, displayCountOffset);
            return {
                kind: 'placed-symbol',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                ...semanticCandidate,
            };
        },
    };
    const pageDisplayPropertyRecordSpec = {
        name: 'page.display-property',
        family: 'page',
        kind: 'display-property',
        match: [(ctx) => ctx.bytes.length >= 20],
        fields: {
            propertyCode: { kind: 'u32le', offset: 0 },
            locX: { kind: 'i16le', offset: 4 },
            locY: { kind: 'i16le', offset: 6 },
            rawFlags: { kind: 'u16le', offset: 8 },
            color: { kind: 'u16le', offset: 10, optional: true },
            colorByte: { kind: 'u8', offset: 10, optional: true },
            textStyleId: { kind: 'u16le', offset: 12, optional: true },
            displayType: { kind: 'u16le', offset: 12, optional: true },
            marker: { kind: 'u8', offset: 14, optional: true },
            countedStrings: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 8, maxLength: 256, requireNullTerminator: false }),
            },
            displayedValueCandidates: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 8 },
        },
        confidence: 'heuristic',
        derive: function materializeDisplayPropertyPageObject(fields, ctx) {
            function materializeCompactDisplayPropertyPageObject(fields, record) {
                const bytes = record.payload;
                if (bytes.length < 20 || bytes.length > 80) {
                    return undefined;
                }
                const propertyCode = numberField(fields, 'propertyCode');
                const locX = numberField(fields, 'locX');
                const locY = numberField(fields, 'locY');
                const rawFlags = numberField(fields, 'rawFlags');
                const color = numberField(fields, 'colorByte');
                const textStyleId = numberField(fields, 'textStyleId');
                const marker = numberField(fields, 'marker');
                if (propertyCode === undefined ||
                    locX === undefined ||
                    locY === undefined ||
                    rawFlags === undefined ||
                    color === undefined ||
                    textStyleId === undefined ||
                    marker === undefined ||
                    propertyCode < 1 ||
                    propertyCode > 0xffff ||
                    ![0x18, 0x21, 0x23, 0x27].includes(marker) ||
                    color > 255 ||
                    textStyleId > 4096 ||
                    !arePlausibleCoordinates([locX, locY])) {
                    return undefined;
                }
                const displayedValue = stringArrayField(fields, 'countedStrings').find((string) => string.offset >= 14 &&
                    string.value.trim().length > 0 &&
                    (typeof ctx.extra?.displayFrameEnd !== 'number' ||
                        record.offset + string.offset + 2 + string.length + (string.hasNullTerminator ? 1 : 0) <=
                            ctx.extra.displayFrameEnd));
                return {
                    kind: 'display-property',
                    offset: record.offset,
                    byteLength: record.byteLength,
                    payloadByteLength: record.payloadByteLength,
                    propertyCode,
                    locX,
                    locY,
                    rotation: rawFlags & 0x03,
                    color,
                    displayType: rawFlags,
                    textStyleId,
                    rawFlags,
                    recordFormat: 'compact',
                    ...(displayedValue ? { displayedValue: displayedValue.value } : {}),
                    ...(displayedValue?.followingUInt32Le !== undefined ?
                        { libraryStringIndex: displayedValue.followingUInt32Le }
                        : {}),
                };
            }
            function materializeCompositeDisplayPropertyPageObject(fields, record) {
                const bytes = record.payload;
                if (bytes.length < 512) {
                    return undefined;
                }
                const propertyCode = numberField(fields, 'propertyCode');
                if (propertyCode !== 0x32fb && propertyCode !== 0x3bdd) {
                    return undefined;
                }
                const locX = numberField(fields, 'locX');
                const locY = numberField(fields, 'locY');
                const rawFlags = numberField(fields, 'rawFlags');
                const color = numberField(fields, 'colorByte');
                const textStyleId = numberField(fields, 'textStyleId');
                const marker = numberField(fields, 'marker');
                if (locX === undefined ||
                    locY === undefined ||
                    rawFlags === undefined ||
                    color === undefined ||
                    textStyleId === undefined ||
                    marker !== 0x40 ||
                    color > 255 ||
                    textStyleId > 4096 ||
                    !arePlausibleCoordinates([locX, locY])) {
                    return undefined;
                }
                return {
                    kind: 'display-property',
                    offset: record.offset,
                    byteLength: record.byteLength,
                    payloadByteLength: record.payloadByteLength,
                    propertyCode,
                    locX,
                    locY,
                    rotation: rawFlags & 0x03,
                    color,
                    displayType: rawFlags,
                    textStyleId,
                    rawFlags,
                    recordFormat: 'composite',
                };
            }
            const record = terminatedPayloadFromContext(ctx);
            const bytes = record.payload;
            if (bytes.length < 20 || bytes.length > 160) {
                return materializeCompositeDisplayPropertyPageObject(fields, record);
            }
            const propertyCode = numberField(fields, 'propertyCode');
            const locX = numberField(fields, 'locX');
            const locY = numberField(fields, 'locY');
            const rotation = numberField(fields, 'rawFlags');
            const color = numberField(fields, 'color');
            const displayType = numberField(fields, 'displayType');
            if (propertyCode === undefined ||
                locX === undefined ||
                locY === undefined ||
                rotation === undefined ||
                color === undefined ||
                displayType === undefined ||
                propertyCode < 1 ||
                propertyCode > 10_000 ||
                rotation > 3 ||
                color > 255 ||
                displayType > 16 ||
                !arePlausibleCoordinates([locX, locY])) {
                return materializeCompactDisplayPropertyPageObject(fields, record);
            }
            if (!hasBytes(bytes, 14, [0x27, 0x22]) && bytes[14] !== 0x18) {
                return materializeCompactDisplayPropertyPageObject(fields, record);
            }
            const displayedValue = stringArrayField(fields, 'countedStrings').find((string) => string.offset >= 14 &&
                string.value.trim().length > 0 &&
                (typeof ctx.extra?.displayFrameEnd !== 'number' ||
                    record.offset + string.offset + 2 + string.length + (string.hasNullTerminator ? 1 : 0) <=
                        ctx.extra.displayFrameEnd));
            return {
                kind: 'display-property',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                propertyCode,
                locX,
                locY,
                rotation,
                color,
                displayType,
                recordFormat: 'standard',
                ...(displayedValue ? { displayedValue: displayedValue.value } : {}),
                ...(displayedValue?.followingUInt32Le !== undefined ?
                    { libraryStringIndex: displayedValue.followingUInt32Le }
                    : {}),
            };
        },
    };
    const pagePinToWireRecordSpec = {
        name: 'page.pin-to-wire',
        family: 'connectivity',
        kind: 'pin-to-wire',
        match: [(ctx) => ctx.extra?.ownedConnectivityFrame === true && ctx.bytes.length >= 16],
        fields: {
            signedPinIndex: { kind: 'i16le', offset: 0 },
            x: { kind: 'i16le', offset: 2 },
            y: { kind: 'i16le', offset: 4 },
            wireDbId: { kind: 'u32le', offset: 6 },
            ownerName: { kind: 'counted-ascii', offset: 16, maxLength: 256, optional: true },
        },
        derive: function materializePinToWirePageObject(fields, ctx) {
            function materializePortInstancePageObject(fields, ctx) {
                const record = terminatedPayloadFromContext(ctx);
                const bytes = record.payload;
                if (ctx.extra?.ownedConnectivityFrame !== true || bytes.length < 16) {
                    return undefined;
                }
                const signedPinIndex = numberField(fields, 'signedPinIndex');
                const x = numberField(fields, 'x');
                const y = numberField(fields, 'y');
                const wireDbId = numberField(fields, 'wireDbId');
                if (signedPinIndex === undefined || x === undefined || y === undefined || wireDbId === undefined) {
                    return undefined;
                }
                const pinIndex = Math.abs(signedPinIndex);
                const isNoConnect = signedPinIndex < 0 || wireDbId === 0;
                if (pinIndex < 1 ||
                    pinIndex > 4096 ||
                    (wireDbId !== 0 && (wireDbId < 100_000 || wireDbId > 1_000_000_000)) ||
                    !arePlausibleCoordinates([x, y])) {
                    return undefined;
                }
                const ownerName = stringField(fields, 'ownerName');
                return {
                    kind: 'port-instance',
                    offset: record.offset,
                    byteLength: record.byteLength,
                    payloadByteLength: record.payloadByteLength,
                    pinIndex,
                    position: pinIndex - 1,
                    ...(signedPinIndex < 0 ? { pinName: String(pinIndex) } : {}),
                    x,
                    y,
                    ...(wireDbId !== 0 ? { wireDbId } : {}),
                    isNoConnect,
                    source: 'record',
                    ...(ownerName ? { ownerName } : {}),
                };
            }
            const portInstance = materializePortInstancePageObject(fields, ctx);
            if (!portInstance ||
                portInstance.kind !== 'port-instance' ||
                portInstance.isNoConnect ||
                portInstance.wireDbId === undefined) {
                return undefined;
            }
            return {
                kind: 'pin-to-wire',
                offset: portInstance.offset,
                byteLength: portInstance.byteLength,
                payloadByteLength: portInstance.payloadByteLength,
                pinIndex: portInstance.pinIndex,
                position: portInstance.position,
                x: portInstance.x,
                y: portInstance.y,
                wireDbId: portInstance.wireDbId,
                isNoConnect: false,
                ...(portInstance.ownerName ? { ownerName: portInstance.ownerName } : {}),
            };
        },
    };
    const pagePortInstanceRecordSpec = {
        name: 'page.port-instance',
        family: 'connectivity',
        kind: 'port-instance',
        match: [(ctx) => ctx.extra?.ownedConnectivityFrame === true && ctx.bytes.length >= 16],
        fields: {
            signedPinIndex: { kind: 'i16le', offset: 0 },
            x: { kind: 'i16le', offset: 2 },
            y: { kind: 'i16le', offset: 4 },
            wireDbId: { kind: 'u32le', offset: 6 },
            ownerName: { kind: 'counted-ascii', offset: 16, maxLength: 256, optional: true },
        },
        derive: function materializePortInstancePageObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const bytes = record.payload;
            if (ctx.extra?.ownedConnectivityFrame !== true || bytes.length < 16) {
                return undefined;
            }
            const signedPinIndex = numberField(fields, 'signedPinIndex');
            const x = numberField(fields, 'x');
            const y = numberField(fields, 'y');
            const wireDbId = numberField(fields, 'wireDbId');
            if (signedPinIndex === undefined || x === undefined || y === undefined || wireDbId === undefined) {
                return undefined;
            }
            const pinIndex = Math.abs(signedPinIndex);
            const isNoConnect = signedPinIndex < 0 || wireDbId === 0;
            if (pinIndex < 1 ||
                pinIndex > 4096 ||
                (wireDbId !== 0 && (wireDbId < 100_000 || wireDbId > 1_000_000_000)) ||
                !arePlausibleCoordinates([x, y])) {
                return undefined;
            }
            const ownerName = stringField(fields, 'ownerName');
            return {
                kind: 'port-instance',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                pinIndex,
                position: pinIndex - 1,
                ...(signedPinIndex < 0 ? { pinName: String(pinIndex) } : {}),
                x,
                y,
                ...(wireDbId !== 0 ? { wireDbId } : {}),
                isNoConnect,
                source: 'record',
                ...(ownerName ? { ownerName } : {}),
            };
        },
    };
    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_RECORD_SPECS = [
        pageWireRecordSpec,
        pageDrawnPortRecordSpec,
        pageAliasRecordSpec,
        pagePlacedSymbolRecordSpec,
        pageDisplayPropertyRecordSpec,
        pagePinToWireRecordSpec,
        pagePortInstanceRecordSpec,
        pageBusEntryRecordSpec,
    ];
    const symbolPinNumberMapInlineRecordSpec = {
        name: 'symbol.pin-number-map-inline',
        family: 'symbol',
        kind: 'pin-number-map',
        match: [(ctx) => ctx.bytes.length >= 12],
        fields: {
            countedStrings: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 128, maxLength: 512 }),
            },
            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 128 },
        },
        derive: function materializeInlinePinNumberMapObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const strings = stringArrayField(fields, 'countedStrings');
            if (strings.length < 3 || strings[0]?.offset !== 0) {
                return undefined;
            }
            const packageName = strings.find((string, index) => index > 0 && (string.value === ctx.streamName || string.offset <= 8));
            if (!packageName) {
                return undefined;
            }
            const entries = strings
                .slice(strings.indexOf(packageName) + 1)
                .filter((string) => /^[A-Za-z0-9_.-]+$/.test(string.value))
                .map((string, position) => ({ position, number: string.value }));
            if (entries.length === 0) {
                return undefined;
            }
            return {
                kind: 'pin-number-map',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                selector: strings[0].value,
                packageName: packageName.value,
                entries,
                strings,
            };
        },
    };
    const VIEW_HEADER_PRIMITIVE_COUNT_MARKER = 0x30;
    function viewHeaderPrimitiveCountFromContext(ctx) {
        const name = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(ctx.bytes, ctx.offset, { maxLength: 512 });
        if (!name) {
            return undefined;
        }
        const sourceLibrary = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readLengthPrefixedAsciiStringAt(ctx.bytes, OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(name), { maxLength: 512 });
        if (!sourceLibrary) {
            return undefined;
        }
        const markerOffset = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.lengthPrefixedStringEnd(sourceLibrary);
        if (markerOffset + 6 > ctx.bytes.length ||
            DsnCfbBinarySpec_1.DsnCfbBinarySpec.readUInt32LE(ctx.bytes, markerOffset) !== VIEW_HEADER_PRIMITIVE_COUNT_MARKER) {
            return undefined;
        }
        return readUInt16LE(ctx.bytes, markerOffset + 4);
    }
    const symbolViewHeaderRecordSpec = {
        name: 'symbol.view-header',
        family: 'symbol',
        kind: 'view-header',
        match: [(ctx) => ctx.bytes.length >= 4],
        fields: {
            name: { kind: 'counted-ascii', offset: 0, maxLength: 512, optional: true },
            countedStrings: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 64, maxLength: 512 }),
            },
            primitiveCount: {
                kind: 'custom',
                offset: 0,
                read: viewHeaderPrimitiveCountFromContext,
            },
            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 64 },
        },
        derive: function materializeSymbolViewHeaderObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const strings = stringArrayField(fields, 'countedStrings');
            const first = strings[0];
            if (!first || first.offset !== 0) {
                return undefined;
            }
            const viewName = strings.find((string) => string.value.endsWith('.Normal') || string.value.endsWith('.Convert'));
            if (!viewName && first.value === ctx.streamName) {
                return undefined;
            }
            if (!isStreamViewName(first.value, ctx.streamName) && !viewName) {
                return undefined;
            }
            const primitiveCount = numberField(fields, 'primitiveCount');
            return {
                kind: 'view-header',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                name: first.value,
                ...(viewName ? { viewName: viewName.value } : {}),
                ...(primitiveCount === undefined ? {} : { primitiveCount }),
                strings,
            };
        },
    };
    const symbolPinRecordSpec = {
        name: 'symbol.pin',
        family: 'symbol',
        kind: 'symbol-pin',
        match: [(ctx) => ctx.bytes.length >= 24],
        fields: {
            name: { kind: 'counted-ascii', offset: 0, maxLength: 256, optional: true },
            dataOffset: {
                kind: 'custom',
                offset: 0,
                read: leadingCountedStringEndFromContext,
            },
            startX: {
                kind: 'i32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset : undefined),
            },
            startY: {
                kind: 'i32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 4 : undefined),
            },
            hotptX: {
                kind: 'i32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 8 : undefined),
            },
            hotptY: {
                kind: 'i32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 12 : undefined),
            },
            displayFlags: {
                kind: 'u16le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 16 : undefined),
                optional: true,
            },
            pinType: {
                kind: 'u32le',
                offset: (ctx) => (typeof ctx.fields?.dataOffset === 'number' ? ctx.fields.dataOffset + 20 : undefined),
                optional: true,
            },
            asciiRuns: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 8 },
        },
        derive: function materializeSymbolPinObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const bytes = record.payload;
            if (bytes.length < 24) {
                return undefined;
            }
            const nameValue = stringField(fields, 'name');
            if (!nameValue || isStreamViewName(nameValue, ctx.streamName)) {
                return undefined;
            }
            const dataOffset = numberField(fields, 'dataOffset');
            if (dataOffset === undefined) {
                return undefined;
            }
            if (dataOffset + 16 > bytes.length) {
                return undefined;
            }
            const startX = numberField(fields, 'startX');
            const startY = numberField(fields, 'startY');
            const hotptX = numberField(fields, 'hotptX');
            const hotptY = numberField(fields, 'hotptY');
            if (startX === undefined || startY === undefined || hotptX === undefined || hotptY === undefined) {
                return undefined;
            }
            if (!arePlausibleCoordinates([startX, startY, hotptX, hotptY])) {
                return undefined;
            }
            const displayFlags = numberField(fields, 'displayFlags');
            const pinType = numberField(fields, 'pinType');
            return {
                kind: 'symbol-pin',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                name: nameValue,
                startX,
                startY,
                hotptX,
                hotptY,
                ...(displayFlags === undefined ? {} : { displayFlags }),
                ...(pinType === undefined ? {} : { pinType }),
            };
        },
    };
    const symbolDisplayPropertyRecordSpec = {
        name: 'symbol.display-property',
        family: 'symbol',
        kind: 'display-property',
        match: [(ctx) => ctx.bytes.length >= 24 && ctx.bytes.length <= 192],
        fields: {
            propertyCode: { kind: 'u32le', offset: 0 },
            locX: { kind: 'i16le', offset: 4 },
            locY: { kind: 'i16le', offset: 6 },
            textStyleId: { kind: 'u16le', offset: 8 },
            color: { kind: 'u8', offset: 10 },
            displayType: { kind: 'u16le', offset: 12 },
            countedStrings: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 32, maxLength: 512 }),
            },
            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 32 },
        },
        confidence: 'heuristic',
        derive: function materializeSymbolDisplayPropertyObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const bytes = record.payload;
            if (bytes.length < 24 || bytes.length > 192) {
                return undefined;
            }
            const propertyCode = numberField(fields, 'propertyCode');
            const locX = numberField(fields, 'locX');
            const locY = numberField(fields, 'locY');
            const textStyleId = numberField(fields, 'textStyleId');
            const color = numberField(fields, 'color');
            const displayType = numberField(fields, 'displayType');
            if (propertyCode === undefined ||
                locX === undefined ||
                locY === undefined ||
                textStyleId === undefined ||
                color === undefined ||
                displayType === undefined) {
                return undefined;
            }
            if (propertyCode < 1 ||
                propertyCode > 10_000 ||
                textStyleId > 4096 ||
                color > 255 ||
                displayType > 16 ||
                !arePlausibleCoordinates([locX, locY])) {
                return undefined;
            }
            const strings = stringArrayField(fields, 'countedStrings');
            if (!hasBytes(bytes, 14, [0x27, 0x22]) && strings.length === 0) {
                return undefined;
            }
            return {
                kind: 'display-property',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                propertyCode,
                locX,
                locY,
                textStyleId,
                color,
                displayType,
                strings,
            };
        },
    };
    const symbolPartValueRecordSpec = {
        name: 'symbol.part-value',
        family: 'symbol',
        kind: 'part-value',
        match: [(ctx) => ctx.bytes.length >= 24],
        fields: {
            countedStrings: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 16, maxLength: 512 }),
            },
            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 16 },
        },
        derive: function materializePartValueObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const strings = stringArrayField(fields, 'countedStrings');
            const referencePrefix = strings.find((string) => string.offset === 20);
            const value = strings.find((string) => string.offset === 24);
            if (!referencePrefix && !value) {
                return undefined;
            }
            return {
                kind: 'part-value',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                ...(referencePrefix ? { referencePrefix: referencePrefix.value } : {}),
                ...(value ? { value: value.value } : {}),
                strings,
            };
        },
    };
    const symbolNamedRecordRecordSpec = {
        name: 'symbol.named-record',
        family: 'symbol',
        kind: 'named-record',
        match: [(ctx) => ctx.bytes.length >= 4],
        fields: {
            name: { kind: 'counted-ascii', offset: 0, maxLength: 512, optional: true },
            countedStrings: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => countedStringsFromContext(ctx, { maxCount: 64, maxLength: 512 }),
            },
            strings: { kind: 'ascii-runs', offset: 0, minLength: 1, maxCount: 64 },
        },
        confidence: 'heuristic',
        derive: function materializeNamedRecordObject(fields, ctx) {
            const record = terminatedPayloadFromContext(ctx);
            const strings = stringArrayField(fields, 'countedStrings');
            const first = strings[0];
            if (!first || first.offset !== 0 || isStreamViewName(first.value, ctx.streamName)) {
                return undefined;
            }
            return {
                kind: 'named-record',
                offset: record.offset,
                byteLength: record.byteLength,
                payloadByteLength: record.payloadByteLength,
                name: first.value,
                strings,
            };
        },
    };
    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_RECORD_SPECS = [
        symbolPinNumberMapInlineRecordSpec,
        symbolViewHeaderRecordSpec,
        symbolPinRecordSpec,
        symbolDisplayPropertyRecordSpec,
        symbolPartValueRecordSpec,
        symbolNamedRecordRecordSpec,
    ];
    const pageDrawnInstScanRecordSpec = {
        name: 'page.drawn-inst-scan',
        family: 'page',
        kind: 'drawn-inst',
        match: [(ctx) => ctx.offset + 24 <= ctx.bytes.length],
        fields: {
            recordedNameIndex: { kind: 'u32le', offset: -11, optional: true, when: (ctx) => ctx.offset >= 11 },
            dbId: { kind: 'u32le', offset: 0 },
            displayY1: { kind: 'i16le', offset: 4 },
            displayX1: { kind: 'i16le', offset: 6 },
            displayY2: { kind: 'i16le', offset: 8 },
            displayX2: { kind: 'i16le', offset: 10 },
            locX: { kind: 'i16le', offset: 12 },
            locY: { kind: 'i16le', offset: 14 },
            color: { kind: 'u8', offset: 16 },
            flags: { kind: 'u8', offset: 17 },
            recordType: { kind: 'u16le', offset: 18 },
        },
        confidence: 'heuristic',
        derive: function materializeDrawnInstPageObject(fields, ctx) {
            const bytes = ctx.bytes;
            const offset = ctx.offset;
            const recordOffset = ctx.payloadOffset ?? offset;
            if (offset + 24 > bytes.length ||
                offset < 7 ||
                !Array.from(bytes.subarray(offset - 7, offset)).every((byte) => byte === 0)) {
                return undefined;
            }
            const dbId = numberField(fields, 'dbId');
            const displayY1 = numberField(fields, 'displayY1');
            const displayX1 = numberField(fields, 'displayX1');
            const displayY2 = numberField(fields, 'displayY2');
            const displayX2 = numberField(fields, 'displayX2');
            const locX = numberField(fields, 'locX');
            const locY = numberField(fields, 'locY');
            const color = numberField(fields, 'color');
            const flags = numberField(fields, 'flags');
            const recordType = numberField(fields, 'recordType');
            const recordedNameIndex = numberField(fields, 'recordedNameIndex');
            if (dbId === undefined ||
                displayY1 === undefined ||
                displayX1 === undefined ||
                displayY2 === undefined ||
                displayX2 === undefined ||
                locX === undefined ||
                locY === undefined ||
                color === undefined ||
                flags === undefined ||
                recordType === undefined) {
                return undefined;
            }
            const mirror = flags & 0x01;
            const rotation = flags & 0x06;
            if (dbId < 1 ||
                dbId > 1_000_000_000 ||
                color !== 48 ||
                flags > 16 ||
                displayX2 < displayX1 ||
                displayY2 < displayY1 ||
                !arePlausibleCoordinates([displayX1, displayY1, displayX2, displayY2, locX, locY])) {
                return undefined;
            }
            if (displayX1 - locX < -100 ||
                displayX1 - locX > 0 ||
                displayY1 - locY < -100 ||
                displayY1 - locY > 0 ||
                displayX2 <= locX ||
                displayY2 <= locY ||
                displayX2 - locX > 5000 ||
                displayY2 - locY > 5000) {
                return undefined;
            }
            const hasDisplayTrailer = offset + 24 <= bytes.length &&
                (hasBytes(bytes, offset + 22, [0x27, 0x22]) || hasBytes(bytes, offset + 22, [0x18, 0x18]));
            if (!hasDisplayTrailer && bytes.length - offset < 40) {
                return undefined;
            }
            const recordedName = recordedNameIndex !== undefined ? libraryCatalogValue(ctx, recordedNameIndex) : undefined;
            return {
                kind: 'drawn-inst',
                offset: recordOffset,
                byteLength: Math.min((ctx.byteLength ?? bytes.length) - offset, 54),
                payloadByteLength: Math.min((ctx.payloadByteLength ?? bytes.length) - offset, 54),
                dbId,
                locX,
                locY,
                color,
                mirror,
                rotation,
                displayX1,
                displayY1,
                displayX2,
                displayY2,
                relativeDisplayX1: displayX1 - locX,
                relativeDisplayY1: displayY1 - locY,
                relativeDisplayX2: displayX2 - locX,
                relativeDisplayY2: displayY2 - locY,
                flags,
                recordType,
                ...(recordedNameIndex !== undefined ? { recordedNameIndex } : {}),
                ...(recordedName !== undefined ? { recordedName } : {}),
            };
        },
    };
    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_PAGE_SCAN_RECORD_SPECS = [pageDrawnInstScanRecordSpec];
    const symbolPinNumberMapTailRecordSpec = {
        name: 'symbol.pin-number-map-tail',
        family: 'symbol',
        kind: 'pin-number-map',
        match: [(ctx) => ctx.offset + 10 <= ctx.bytes.length],
        fields: {
            pinNumberMap: {
                kind: 'custom',
                offset: 0,
                read: (ctx) => tailPinNumberMapFromContext(ctx),
            },
            strings: {
                kind: 'ascii-runs',
                offset: 0,
                minLength: 1,
                maxCount: 128,
                when: (ctx) => ctx.fields?.pinNumberMap !== undefined,
                byteLength: (ctx) => {
                    const map = ctx.fields?.pinNumberMap;
                    return map === undefined ? undefined : map.offset - ctx.offset + map.byteLength;
                },
            },
        },
        derive: function materializeTailPinNumberMapObject(fields, _ctx) {
            const map = fields.pinNumberMap;
            if (!map) {
                return undefined;
            }
            return {
                kind: 'pin-number-map',
                ...map,
            };
        },
    };
    OrcadDsnCfbDeclarativeSpecs.ORCAD_DSN_CFB_SYMBOL_TAIL_RECORD_SPECS = [symbolPinNumberMapTailRecordSpec];
})(OrcadDsnCfbDeclarativeSpecs || (exports.OrcadDsnCfbDeclarativeSpecs = OrcadDsnCfbDeclarativeSpecs = {}));
//# sourceMappingURL=OrcadDsnCfbDeclarativeSpecs.js.map